import { useState, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import styled from "styled-components/macro";
import { useProjectStore } from "../../../../stores/projectStore";
import { useSeriesStore } from "../../../../stores/seriesStore";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { useStructuredOutlineStore } from "../StructuredOutlineStore";
import { useToast } from "../../../ToastWrapper";
import SimplifiedTenseAndPOV from "../../TenseAndPOV/Simplified";
import SimplifiedApplyToSelectorButton from "../../TenseAndPOV/Simplified/applyToSelectorButton";
import { sendAppEvent } from "../../../../Utils";
import { POVS, isAutoPOV } from "../../../../../../src/shared/constants";
import { useOutlinePOVTense } from "../useOutlinePOVTense";
import { createPOVTenseObject, DEFAULT_POV_TENSE_OBJECT } from "../../../../../../src/shared/constants";

const BottomContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${(props) => (props.alignLeft ? "flex-start" : props.alignCenter ? "space-between" : "flex-end")};
  gap: 17px;
  padding: 16px;
`;

export const POVTenseBar = ({ projectId }) => {
  const project = useProjectStore((state) => state.project);
  const selectedSeriesProjectId = useSeriesStore((state) => state.selectedSeriesProjectId);
  const getPOVTenseForSeriesProject = useSeriesStore((state) => state.getPOVTenseForSeriesProject);
  const updatePOVTenseForSeriesProject = useSeriesStore((state) => state.updatePOVTenseForSeriesProject);
  const document = useProjectStore((state) => state.document);
  const documentPOVTenseStatus = useProjectStore((state) => state.currentDocumentPOVTenseStatus);
  const retryDocumentPOVTense = useProjectStore((state) => state.retryDocumentPOVTense);
  const currentDocumentPOVTense = useProjectStore((state) => state.currentDocumentPOVTense);
  const selectedEntry = useSeriesStore((state) => state.contents.find((entry) => entry.projectId === selectedSeriesProjectId));
  const { isMultiplePOV: selectedMode, povTense: wholeBookPOVTense } = useOutlinePOVTense();
  const multiplePOV = selectedMode?.value || false;
  const multiplePOVUserChanged = selectedMode?.userChanged || false;
  const setMultiplePOV = useProjectStore((state) => state.setMultiplePOV);
  const projectPOVTense = useProjectStore((state) => state.povTense);
  const setPOVTenseForProject = useProjectStore((state) => state.setPOVTense);
  const setPOVTenseForDocument = useProjectStore((state) => state.setPOVTenseForDocument);

  const [localPOVTense, setPOVTense] = useState({
    pov: { value: null, characters: [], userChanged: false },
    tense: { value: null, userChanged: false },
  });
  const [multiPOV, setMultiPOV] = useState(multiplePOV);
  const editEpoch = useRef(0);
  const usesDocumentSettings = multiplePOV && (!project.seriesId || selectedSeriesProjectId === project.id);
  const documentSettingsUnavailable = usesDocumentSettings && documentPOVTenseStatus !== "ready";
  // Use the current document snapshot immediately; a series refresh or effect
  // must not expose an older local copy between the read and the next render.
  const povTense = usesDocumentSettings && currentDocumentPOVTense ? currentDocumentPOVTense : localPOVTense;
  const identity = `${project.id}/${project.seriesId}/${selectedSeriesProjectId}`;
  const selectionGeneration = useRef(0);
  const [initializedGeneration, setInitializedGeneration] = useState(null);
  const mounted = useRef(true);
  const [, selectionChanged] = useState(0);
  const currentIdentity = useRef(identity);
  if (currentIdentity.current !== identity) {
    currentIdentity.current = identity;
    selectionGeneration.current++;
    editEpoch.current++;
  }
  const generation = selectionGeneration.current;
  const ready = initializedGeneration === generation && !documentSettingsUnavailable;
  // Check the generation in callbacks too: a previously open control can retain
  // a callback from before the selected book changed.
  const canEdit = () => mounted.current && ready && generation === selectionGeneration.current
    && useProjectStore.getState().project?.id === project.id
    && (!usesDocumentSettings || (useProjectStore.getState().document?.id === document?.id
      && useProjectStore.getState().currentDocumentPOVTenseStatus === "ready"))
    && (!project.seriesId || useSeriesStore.getState().selectedSeriesProjectId === selectedSeriesProjectId);
  useEffect(() => {
    mounted.current = true;
    const invalidate = () => { editEpoch.current++; };
    const unsubscribe = useSeriesStore.subscribe((state, previous) => {
      if (state.selectedSeriesProjectId !== previous.selectedSeriesProjectId) {
        selectionGeneration.current++;
        invalidate();
        selectionChanged((value) => value + 1);
      }
    });
    return () => { mounted.current = false; invalidate(); unsubscribe(); };
  }, []);
  const beginEdit = () => {
    const epoch = ++editEpoch.current;
    return () => canEdit() && epoch === editEpoch.current && currentIdentity.current === identity;
  };

  const updateItemPovTense = useStructuredOutlineStore((state) => state.updateItemPovTense);
  const povTenseMap = useStructuredOutlineStore((state) => state.povTenseMap);
  const { setToast } = useToast();
  const scenes = useStoryBibleStore((state) => state.scenes);

  const syncProjectPOVTenseToChapters = async (snapshot, isCurrent) => {
    const basePov = snapshot?.pov;
    const baseTense = snapshot?.tense;
    const hasPovSelection = !!basePov?.value;
    const hasTenseSelection = !!baseTense?.value;

    if (!hasPovSelection && !hasTenseSelection) {
      return;
    }

    const store = useStructuredOutlineStore.getState();
    const context = { ...store.getPOVTenseContext(), isCurrent };
    const owner = project.seriesId ? selectedSeriesProjectId : project.id;
    if (!canEdit() || context.projectId !== owner) throw new Error("Chapter synchronization interrupted by book change");
    const outlineItems = store.outline?.content?.items || [];
    const chapters = outlineItems.filter((item) => item.type === "chapter");
    if (chapters.length === 0) {
      return;
    }

    const baseCharacters = Array.isArray(basePov?.characters) ? basePov.characters : [];

    await Promise.all(
      chapters.map((chapter) => {
        const existing = povTenseMap?.[chapter.povTenseId];
        return updateItemPovTense(
          chapter.id,
          chapter.povTenseId,
          {
            pov: hasPovSelection ? { ...basePov, characters: [...baseCharacters] } : existing?.pov || DEFAULT_POV_TENSE_OBJECT.pov,
            tense: hasTenseSelection ? { ...baseTense } : existing?.tense || DEFAULT_POV_TENSE_OBJECT.tense,
          },
          scenes,
          context
        );
      })
    );
  };

  useEffect(() => {
    if (!project.seriesId || !selectedSeriesProjectId) return;
    let cancelled = false;
    const epoch = editEpoch.current;
    getPOVTenseForSeriesProject(selectedSeriesProjectId).then((settings) => {
      if (cancelled || epoch !== editEpoch.current || generation !== selectionGeneration.current) return;
      if (!settings) {
        return;
      }
      const mode = settings.isMultiplePOV?.value ?? false;
      setMultiPOV(mode);
      // Reloads can start after an edit while its subscription check is pending.
      // Read the latest intents at completion, before they reach the contents cache.
      const values = mode && selectedSeriesProjectId === project.id && currentDocumentPOVTense
        ? currentDocumentPOVTense : useSeriesStore.getState().getIntendedPOVTenseForSeriesProject(selectedSeriesProjectId);
      setPOVTense(createPOVTenseObject(values?.pov, values?.tense));
      setInitializedGeneration(generation);
    }).catch((error) => {
      if (!cancelled && epoch === editEpoch.current && generation === selectionGeneration.current) {
        Sentry.captureException(error, { tags: { feature: "structured_outline_pov_load" } });
      }
    });
    return () => { cancelled = true; };
  }, [project.seriesId, project.id, selectedSeriesProjectId, selectedEntry, currentDocumentPOVTense, getPOVTenseForSeriesProject, generation]);

  const handleApplyToChange = async (selectedOption) => {
    if (!canEdit()) return;
    const isCurrent = beginEdit();
    const nextIsMultiple = selectedOption === "specific-chapters";
    const shouldSyncChapters = nextIsMultiple && !multiplePOVUserChanged;
    // userChanged is also the one-time synchronization guard. Persist it only
    // after synchronization succeeds, so interruption leaves an explicit retry.
    const interruptedSync = multiplePOV && !multiplePOVUserChanged;
    const nextMultipleState = { value: nextIsMultiple, userChanged: !shouldSyncChapters && !interruptedSync };
    const previousMultipleState = selectedMode || { value: false, userChanged: false };
    const intended = project.seriesId
      ? useSeriesStore.getState().getIntendedPOVTenseForSeriesProject(selectedSeriesProjectId)
      : wholeBookPOVTense;
    const snapshot = createPOVTenseObject(intended?.pov, intended?.tense);
    const saveMode = (mode, values = snapshot) => project.seriesId
      ? updatePOVTenseForSeriesProject(selectedSeriesProjectId, values, mode)
      : setMultiplePOV(mode);

    try {
      await saveMode(nextMultipleState, {
        pov: { ...snapshot.pov, userChanged: true },
        tense: { ...snapshot.tense, userChanged: true },
      });
      if (!isCurrent()) return;
      // The standalone setter handles its own failure and restores the old mode.
      const enabled = project.seriesId ? nextIsMultiple : useProjectStore.getState().isMultiplePOV?.value;
      setMultiPOV(!!enabled);
      if (!shouldSyncChapters || !enabled) return;
      try {
        await syncProjectPOVTenseToChapters(snapshot, isCurrent);
        if (isCurrent()) await saveMode({ value: true, userChanged: true }, {});
      } catch (error) {
        // Chapter writes can partially succeed. Restore the mode for an explicit retry,
        // but never restore an old snapshot over a newer edit or selected book.
        if (isCurrent()) {
          await saveMode(previousMultipleState, {});
          if (isCurrent()) setMultiPOV(previousMultipleState.value);
        }
        Sentry.captureException(error, { tags: { feature: "structured_outline_multi_pov_initial_sync" } });
        setToast("Could not sync chapter POV and tense. Please try again.");
      }
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: "structured_outline_mode_update" } });
    }
  };

  const handleUpdateProjectPOV = async (selectedOption) => {
    if (!canEdit()) return;
    beginEdit();
    sendAppEvent("changing_project_pov_from_outline", {
      isMultiplePOV: multiPOV,
      isSeries: !!project.seriesId,
      pov: selectedOption,
      projectId,
    });

    const isAutoPOVSelected = isAutoPOV(selectedOption);
    const hasCharacterSelection = povTense?.pov?.characters?.length > 0;

    try {
      if (project.seriesId && (!multiPOV || selectedSeriesProjectId !== project.id)) {
        setPOVTense((prev) => ({
          ...prev,
          pov: { ...povTense.pov, value: selectedOption, characters: isAutoPOVSelected && hasCharacterSelection ? [] : povTense.pov.characters, userChanged: true },
        }));
        await updatePOVTenseForSeriesProject(selectedSeriesProjectId, {
          pov: { ...povTense.pov, value: selectedOption, characters: isAutoPOVSelected && hasCharacterSelection ? [] : povTense.pov.characters, userChanged: true },
        });
        return;
      }

      if (!multiPOV) {
        await setPOVTenseForProject({
          ...povTense,
          pov: { ...povTense.pov, value: selectedOption, characters: isAutoPOVSelected && hasCharacterSelection ? [] : povTense.pov.characters, userChanged: true },
        });
        return;
      }
      setPOVTense((prev) => ({ ...prev, pov: { ...prev.pov, value: selectedOption, characters: isAutoPOVSelected && hasCharacterSelection ? [] : povTense.pov.characters, userChanged: true } }));
      await setPOVTenseForDocument({
        pov: { ...povTense.pov, value: selectedOption, characters: isAutoPOVSelected && hasCharacterSelection ? [] : povTense.pov.characters, userChanged: true },
        tense: povTense.tense,
      });
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: "structured_outline_pov_update" } });
    }
  };

  const handleUpdateProjectTense = async (selectedOption) => {
    if (!canEdit()) return;
    const isCurrent = beginEdit();
    sendAppEvent("changing_project_tense_from_outline", {
      isMultiplePOV: multiPOV,
      isSeries: !!project.seriesId,
      tense: selectedOption,
      projectId,
    });

    try {
      if (project.seriesId && (!multiPOV || selectedSeriesProjectId !== project.id)) {
        setPOVTense((prev) => ({
          ...prev,
          tense: selectedOption,
        }));
        await updatePOVTenseForSeriesProject(selectedSeriesProjectId, { tense: selectedOption });
        return;
      }

      if (!multiPOV) {
        await setPOVTenseForProject({ ...povTense, tense: selectedOption });
        return;
      }
      const updatedValues = await setPOVTenseForDocument({
        tense: selectedOption,
        pov: povTense.pov,
      });
      if (isCurrent()) setPOVTense((prev) => ({ ...prev, tense: updatedValues.tense }));
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: "structured_outline_tense_update" } });
    }
  };

  const handleCharacterSelectChange = async (selectedOptions) => {
    if (!canEdit()) return;
    beginEdit();
    sendAppEvent("changing_project_characters_from_outline", {
      isMultiplePOV: multiPOV,
      isSeries: !!project.seriesId,
      characters: selectedOptions,
      projectId,
    });

    const isAutoPOVSelected = isAutoPOV(povTense.pov.value);
    const hasCharacterSelection = selectedOptions.length > 0;

    const nextPov = {
      ...povTense.pov,
      value: isAutoPOVSelected && hasCharacterSelection ? POVS.THIRD_PERSON_LIMITED : povTense.pov.value,
      characters: selectedOptions,
      userChanged: true,
    };

    if (project.seriesId && (!multiPOV || selectedSeriesProjectId !== project.id)) {
      setPOVTense((prev) => ({
        ...prev,
        pov: nextPov,
      }));
      await updatePOVTenseForSeriesProject(selectedSeriesProjectId, {
        pov: nextPov,
      });
      return;
    }

    if (!multiPOV) {
      await setPOVTenseForProject({
        ...povTense,
        pov: nextPov,
      });
      return;
    }
    await setPOVTenseForDocument({
      pov: nextPov,
      tense: povTense.tense,
    });
  };

  useEffect(() => {
    if (project.seriesId) return;
    setPOVTense(multiplePOV && currentDocumentPOVTense ? currentDocumentPOVTense : projectPOVTense || DEFAULT_POV_TENSE_OBJECT);
    setMultiPOV(multiplePOV);
    setInitializedGeneration(generation);
  }, [project.seriesId, multiplePOV, projectPOVTense, currentDocumentPOVTense, generation]);

  return (
    <BottomContainer
      alignCenter={true}
      className={`flex pl-6 gap-4 items-center justify-start flex-wrap ${project.seriesId ? "justify-between" : "[&]:gap-0"}`}
      style={{ containerType: "inline-size", contain: "layout" }}
    >
      {!ready ? <div className="flex items-center gap-2">
        <span role="status">{documentSettingsUnavailable && documentPOVTenseStatus === "error"
          ? "Could not load this document’s POV and tense."
          : "Loading POV and tense settings…"}</span>
        {documentSettingsUnavailable && documentPOVTenseStatus === "error" && (
          <button type="button" className="underline" onClick={retryDocumentPOVTense}>Retry</button>
        )}
      </div> : <div className="flex items-center gap-1 flex-wrap">
        <div className={`${multiPOV ? "opacity-100" : "opacity-100"} flex items-center gap-2 ${project.seriesId ? "mr-2" : ""} @[700px]:mb-0`}>
          <SimplifiedApplyToSelectorButton
            value={multiPOV ? "specific-chapters" : "whole-book"}
            onChange={handleApplyToChange}
            collisionBoundarySelector=".project-main"
            className="!w-[380px]"
            inClassName={`${project.seriesId ? "pl-1 pr-0" : "pl-1 pr-2"}`}
          />
        </div>

        <SimplifiedTenseAndPOV
          pov={povTense?.pov}
          tense={povTense?.tense}
          handleManualPOVChange={handleUpdateProjectPOV}
          handleManualTenseChange={handleUpdateProjectTense}
          handleCharacterSelectChange={handleCharacterSelectChange}
          oneOrManyCharacters="many"
          className="!gap-[18px]"
          collisionBoundarySelector=".project-main"
          showPOVLabel={false}
          showTenseLabel={false}
          allowWrap={true}
        />
      </div>}
    </BottomContainer>
  );
};
